"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var _a;
Object.defineProperty(exports, "__esModule", { value: true });
exports.clearFetchedTimeRanges = exports.updateFetchedTimeRanges = exports.resetTimeRange = exports.resetFixedMeasurementExtremes = exports.updateStreamMeasurements = exports.resetStreamMeasurements = exports.resetLastSelectedTimeRange = exports.setLastSelectedTimeRange = exports.resetFixedStreamState = exports.updateFixedMeasurementExtremes = exports.checkDataAvailability = exports.fetchMeasurements = exports.fetchFixedStreamById = exports.initialState = void 0;
const toolkit_1 = require("@reduxjs/toolkit");
const apiClient_1 = require("../api/apiClient");
const apiEndpoints_1 = require("../api/apiEndpoints");
const timeRange_1 = require("../types/timeRange");
const cookieManager_1 = require("../utils/cookieManager");
const getErrorMessage_1 = require("../utils/getErrorMessage");
const logController_1 = require("../utils/logController");
exports.initialState = {
    data: {
        stream: {
            lastUpdate: null,
            updateFrequency: "",
            startTime: "",
            endTime: null,
            profile: "",
            sensorName: "",
            sessionId: 0,
            title: "",
            unitSymbol: "",
            active: true,
            min: 0,
            low: 0,
            middle: 0,
            high: 0,
            max: 0,
            latitude: 0,
            longitude: 0,
        },
        measurements: [],
        streamDailyAverages: [],
        lastMonthMeasurements: [],
    },
    fetchedStartTime: null,
    minMeasurementValue: null,
    maxMeasurementValue: null,
    averageMeasurementValue: null,
    status: "IDLE" /* StatusEnum.Idle */,
    error: null,
    isLoading: false,
    lastSelectedTimeRange: (() => {
        // Load from localStorage only if preference cookies are allowed
        if (typeof window !== "undefined" &&
            cookieManager_1.CookieManager.arePreferenceCookiesAllowed()) {
            const saved = localStorage.getItem("lastSelectedTimeRange");
            if (saved &&
                Object.values(timeRange_1.FixedTimeRange).includes(saved)) {
                return saved;
            }
        }
        return timeRange_1.FixedTimeRange.Day;
    })(),
    measurements: {},
    fetchedTimeRanges: {},
};
exports.fetchFixedStreamById = (0, toolkit_1.createAsyncThunk)("fixedStream/getData", (id, { rejectWithValue }) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        const response = yield apiClient_1.apiClient.get(apiEndpoints_1.API_ENDPOINTS.fetchFixedStreamById(id));
        return response.data;
    }
    catch (error) {
        const message = (0, getErrorMessage_1.getErrorMessage)(error);
        const apiError = {
            message,
            additionalInfo: {
                action: "fetchFixedStreamById",
                endpoint: apiEndpoints_1.API_ENDPOINTS.fetchFixedStreamById(id),
            },
        };
        (0, logController_1.logError)(error, apiError);
        return rejectWithValue(apiError);
    }
}));
exports.fetchMeasurements = (0, toolkit_1.createAsyncThunk)("fixedStream/fetchMeasurements", (params, { rejectWithValue }) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        const response = yield apiClient_1.oldApiClient.get(apiEndpoints_1.API_ENDPOINTS.fetchMeasurements(params.streamId, params.startTime, params.endTime));
        return response.data;
    }
    catch (error) {
        return rejectWithValue((0, getErrorMessage_1.getErrorMessage)(error));
    }
}));
exports.checkDataAvailability = (0, toolkit_1.createAsyncThunk)("fixedStream/checkDataAvailability", ({ streamId, start, end }, { getState }) => __awaiter(void 0, void 0, void 0, function* () {
    const state = getState();
    const fetchedRanges = state.fixedStream.fetchedTimeRanges[streamId] || [];
    return fetchedRanges.some((range) => range.start <= start && range.end >= end);
}));
const fixedStreamSlice = (0, toolkit_1.createSlice)({
    name: "fixedStream",
    initialState: exports.initialState,
    reducers: {
        updateFixedMeasurementExtremes(state, action) {
            const { min, max } = action.payload;
            const measurements = state.data.measurements || [];
            // Filter measurements within the visible range
            const visibleMeasurements = measurements.filter((m) => m.time >= min && m.time <= max);
            if (visibleMeasurements.length > 0) {
                // Calculate min, max, and average in a single pass
                const { min: minValue, max: maxValue, sum, } = visibleMeasurements.reduce((acc, measurement) => ({
                    min: Math.min(acc.min, measurement.value),
                    max: Math.max(acc.max, measurement.value),
                    sum: acc.sum + measurement.value,
                }), {
                    min: visibleMeasurements[0].value,
                    max: visibleMeasurements[0].value,
                    sum: 0,
                });
                state.minMeasurementValue = minValue;
                state.maxMeasurementValue = maxValue;
                state.averageMeasurementValue = sum / visibleMeasurements.length;
            }
            else {
                state.minMeasurementValue = null;
                state.maxMeasurementValue = null;
                state.averageMeasurementValue = null;
            }
        },
        resetFixedStreamState(state) {
            state.lastSelectedTimeRange = timeRange_1.FixedTimeRange.Day;
            if (cookieManager_1.CookieManager.arePreferenceCookiesAllowed()) {
                localStorage.setItem("lastSelectedTimeRange", timeRange_1.FixedTimeRange.Day);
            }
            return exports.initialState;
        },
        setLastSelectedTimeRange(state, action) {
            state.lastSelectedTimeRange = action.payload;
            if (cookieManager_1.CookieManager.arePreferenceCookiesAllowed()) {
                localStorage.setItem("lastSelectedTimeRange", action.payload);
            }
        },
        resetLastSelectedTimeRange(state) {
            state.lastSelectedTimeRange = timeRange_1.FixedTimeRange.Day;
            if (cookieManager_1.CookieManager.arePreferenceCookiesAllowed()) {
                localStorage.setItem("lastSelectedTimeRange", timeRange_1.FixedTimeRange.Day);
            }
        },
        resetStreamMeasurements(state, action) {
            state.data.measurements = [];
        },
        updateStreamMeasurements(state, action) {
            const { measurements } = action.payload;
            const existingMeasurements = state.data.measurements || [];
            const existingMap = new Map(existingMeasurements.map((m) => [m.time, m]));
            measurements.forEach((measurement) => {
                if (!existingMap.has(measurement.time)) {
                    existingMap.set(measurement.time, measurement);
                }
            });
            state.data.measurements = Array.from(existingMap.values()).sort((a, b) => a.time - b.time);
        },
        resetFixedMeasurementExtremes(state) {
            state.minMeasurementValue = null;
            state.maxMeasurementValue = null;
            state.averageMeasurementValue = null;
        },
        resetTimeRange: (state) => {
            state.lastSelectedTimeRange = timeRange_1.FixedTimeRange.Day;
            localStorage.setItem("lastSelectedTimeRange", timeRange_1.FixedTimeRange.Day);
        },
        updateFetchedTimeRanges: (state, action) => {
            const { streamId, start, end } = action.payload;
            if (!state.fetchedTimeRanges[streamId]) {
                state.fetchedTimeRanges[streamId] = [];
            }
            state.fetchedTimeRanges[streamId].push({ start, end });
        },
        clearFetchedTimeRanges: (state, action) => {
            const streamId = action.payload;
            if (state.fetchedTimeRanges[streamId]) {
                state.fetchedTimeRanges[streamId] = [];
            }
        },
    },
    extraReducers: (builder) => {
        builder.addCase(exports.fetchFixedStreamById.pending, (state) => {
            state.status = "PENDING" /* StatusEnum.Pending */;
            state.error = null;
            state.isLoading = true;
        });
        builder.addCase(exports.fetchFixedStreamById.fulfilled, (state, action) => {
            state.status = "FULFILLED" /* StatusEnum.Fulfilled */;
            state.data = action.payload;
            state.isLoading = false;
            state.error = null;
        });
        builder.addCase(exports.fetchFixedStreamById.rejected, (state, action) => {
            state.status = "REJECTED" /* StatusEnum.Rejected */;
            state.error = action.payload || {
                message: "Unknown error occurred fetching stream",
            };
            state.data = exports.initialState.data;
            state.isLoading = false;
        });
        builder.addCase(exports.fetchMeasurements.pending, (state) => {
            state.status = "PENDING" /* StatusEnum.Pending */;
            state.error = null;
            state.isLoading = true;
        });
        builder.addCase(exports.fetchMeasurements.fulfilled, (state, action) => {
            var _a, _b, _c, _d, _e, _f;
            state.status = "FULFILLED" /* StatusEnum.Fulfilled */;
            state.error = null;
            const streamId = Number((_b = (_a = action.meta) === null || _a === void 0 ? void 0 : _a.arg) === null || _b === void 0 ? void 0 : _b.streamId);
            if (streamId) {
                const existingMeasurements = state.data.measurements || [];
                const existingMap = new Map(existingMeasurements.map((m) => [m.time, m]));
                action.payload.forEach((measurement) => {
                    if (!existingMap.has(measurement.time)) {
                        existingMap.set(measurement.time, measurement);
                    }
                });
                state.data.measurements = Array.from(existingMap.values()).sort((a, b) => a.time - b.time);
                // After updating measurements, calculate extremes for visible range if we have measurements
                if (state.data.measurements.length > 0) {
                    // Use the time range from the fetch if available
                    const startTime = Number((_d = (_c = action.meta) === null || _c === void 0 ? void 0 : _c.arg) === null || _d === void 0 ? void 0 : _d.startTime);
                    const endTime = Number((_f = (_e = action.meta) === null || _e === void 0 ? void 0 : _e.arg) === null || _f === void 0 ? void 0 : _f.endTime);
                    // Filter measurements within the visible range
                    const visibleMeasurements = state.data.measurements.filter((m) => m.time >= startTime && m.time <= endTime);
                    if (visibleMeasurements.length > 0) {
                        // Calculate min, max, and average in a single pass
                        const { min: minValue, max: maxValue, sum, } = visibleMeasurements.reduce((acc, measurement) => ({
                            min: Math.min(acc.min, measurement.value),
                            max: Math.max(acc.max, measurement.value),
                            sum: acc.sum + measurement.value,
                        }), {
                            min: visibleMeasurements[0].value,
                            max: visibleMeasurements[0].value,
                            sum: 0,
                        });
                        state.minMeasurementValue = minValue;
                        state.maxMeasurementValue = maxValue;
                        state.averageMeasurementValue = sum / visibleMeasurements.length;
                    }
                }
            }
        });
        builder.addCase(exports.fetchMeasurements.rejected, (state, action) => {
            state.status = "REJECTED" /* StatusEnum.Rejected */;
            state.error = {
                message: (action.error && action.error.message) ||
                    "Unknown error occurred fetching measurements",
            };
        });
        builder.addCase(exports.resetLastSelectedTimeRange, (state) => {
            state.lastSelectedTimeRange = timeRange_1.FixedTimeRange.Day;
        });
    },
});
_a = fixedStreamSlice.actions, exports.updateFixedMeasurementExtremes = _a.updateFixedMeasurementExtremes, exports.resetFixedStreamState = _a.resetFixedStreamState, exports.setLastSelectedTimeRange = _a.setLastSelectedTimeRange, exports.resetLastSelectedTimeRange = _a.resetLastSelectedTimeRange, exports.resetStreamMeasurements = _a.resetStreamMeasurements, exports.updateStreamMeasurements = _a.updateStreamMeasurements, exports.resetFixedMeasurementExtremes = _a.resetFixedMeasurementExtremes, exports.resetTimeRange = _a.resetTimeRange, exports.updateFetchedTimeRanges = _a.updateFetchedTimeRanges, exports.clearFetchedTimeRanges = _a.clearFetchedTimeRanges;
exports.default = fixedStreamSlice.reducer;
